import type { EvmChainId, EvmMintAbiCollectionType } from '@/service/EVM/config/types';
import { type Config, getBalance, readContract, writeContract } from '@wagmi/core';
import { type Address, type Hash } from 'viem';
import { onceUponTomorrowAbi } from '~/service/EVM/abi/mint/onceUponTomorow';

/**
 * @param config - web3 config
 * @param slug - collection slug
 * @param chainId - evm chain id
 * @param address - collection contract address
 */

export interface EvmMintInterface {
    config: Config;
    slug: string;
    chainId?: EvmChainId;
    address: Address;
}

export default class EvmMintFactory {
    config;
    chainId;
    slug;
    address;
    abi: EvmMintAbiCollectionType;

    constructor({
        address,
        config,
        slug,
        chainId
    }: EvmMintInterface) {
        this.config = config;
        this.chainId = chainId;
        this.slug = slug;
        this.abi = onceUponTomorrowAbi;
        this.address = address;
    }

    async getTransactionMintQuantity() {
        try {
            return await readContract(this.config, {
                address: this.address,
                abi: this.abi,
                functionName: 'maxHelmetPurchase'
            });
        }
        catch (e) {
            throw new Error('Error receiving quantity for minting');
        }
    }

    async calculateMintableTokens() {
        try {
            const maxAmount = await readContract(this.config, {
                address: this.address,
                abi: this.abi,
                functionName: 'MAX_HELMETS'
            });
            const totalSupply = await readContract(this.config, {
                address: this.address,
                abi: this.abi,
                functionName: 'totalSupply'
            });

            return {
                maxAmount,
                totalSupply
            };
        }
        catch (e) {
            throw new Error('Error calculating remaining unmined tokens');
        }
    }

    async getTokenPriceFromContract(chainId: EvmChainId) {
        try {
            return await readContract(this.config, {
                address: this.address,
                abi: this.abi,
                functionName: 'helmetPrice',
                chainId
            });
        }
        catch (e) {
            throw new Error('Price retrieval error');
        }
    }

    async checkSaleStatus() {
        try {
            return await readContract(this.config, {
                address: this.address,
                abi: this.abi,
                functionName: 'saleIsActive'
            });
        }
        catch (e) {
            throw new Error('Error retrieving sale status');
        }
    }

    async mint({ value, amount, user }: {
        value: bigint;
        amount: number;
        user?: Address;
    }): Promise<Hash> {
        if (!user) throw new Error('Please log into your wallet.');

        const isActive = await this.checkSaleStatus();

        if (!isActive) throw new Error('Token sale is not active');

        const { maxAmount, totalSupply } = await this.calculateMintableTokens();

        if ((maxAmount - totalSupply) === 0n) throw new Error('The token sale has been finalized');

        const maxQuantity = await this.getTransactionMintQuantity();

        if (amount > maxQuantity) throw new Error(`The token sale has been finalized`);

        const { value: balance } = await getBalance(
            this.config,
            {
                chainId: this.chainId,
                address: user
            }
        );

        const totalValue = value * BigInt(amount);

        if (balance < totalValue) {
            throw new Error(`Not enough tokens on the balance`);
        }

        try {
            return await writeContract(this.config, {
                address: this.address,
                functionName: 'mintHelmets',
                abi: this.abi,
                args: [BigInt(amount)],
                value: totalValue
            });
        }
        catch (e) {
            throw new Error(`Error when performing a minting`);
        }
    }
}
